import React, { ReactElement } from 'react';
import { NextApiRequestQuery } from 'next/dist/server/api-utils';
import { ServerSideInitProps } from 'types/types';
import { PersonBaseInterface } from 'components/interfaces/PersonInterface';
import { ConfigPageInterface } from 'utils/helpers/server/serverSideProps/config';
import {
    CoachClubListItemApiInterface,
    CoachCompetitionApiInterface,
    CoachDetailApiInterface,
    CoachMatchListItemApiInterface,
    PaginatorInterface,
} from 'submodules/api_middleware';
import { getData } from 'utils/helpers/server/serverSideData';
import {
    EMPTY_PAGINATOR,
    PaginatorPageInterface,
} from 'components/interfaces/Paginator';
import CoachMatchesPage from 'components/pages/coach/detail/CoachMatchesPage';
import { Config_CoachMatches_ApiInterface } from 'submodules/api_middleware/src/interfaces/config/CoachMatchesConfigApiInterface';
import { getPureQuery } from 'submodules/api_middleware/src/client';
import { getGlobalProps } from 'utils/helpers/server/serverSideProps/global';
import { getPageProps } from 'utils/helpers/server/serverSideProps/ssrProps';
import { getPageNamespaces } from 'utils/helpers/server/serverSideProps/translations';
import { buildCoachPersonBase } from 'utils/helpers/server/serverSideProps/coachPersonBase';
import { buildCoachMatchesConfigStub } from 'utils/helpers/server/serverSideProps/coachConfig';
import {
    isCoachesRequestAllowed,
    isCoachesPublicRequest,
} from 'utils/helpers/server/coachesAccess';
import { serverRedirect } from 'utils/helpers/server/serverRedirect';
import {
    CompetitionInterface,
    CompetitionYearInterface,
} from 'components/interfaces/GeneralInterface';
import { ClubListItemInterface } from 'components/interfaces/ClubInterface';
import { buildCoachMatchesFilterQuery } from 'utils/helpers/coachMatchesFilter';

interface Props
    extends PaginatorPageInterface,
        ConfigPageInterface<Config_CoachMatches_ApiInterface> {
    personBase: PersonBaseInterface;
    coach: CoachDetailApiInterface;
    matches: CoachMatchListItemApiInterface[];
    competitionList: CompetitionInterface[];
    competitionYears: CompetitionYearInterface[];
    clubList: ClubListItemInterface[];
    filterInitQuery: NextApiRequestQuery;
}

interface ServerSideProps {
    props?: Props;
    notFound?: boolean;
}

const Page = ({
    coach,
    config,
    personBase,
    matches,
    paginator,
    competitionList,
    competitionYears,
    clubList,
    filterInitQuery,
}: Props): ReactElement => (
    <CoachMatchesPage
        coach={coach}
        personBase={personBase}
        config={config}
        matches={matches}
        paginator={paginator}
        competitionList={competitionList}
        competitionYears={competitionYears}
        clubList={clubList}
        filterInitQuery={filterInitQuery}
    />
);

export const getServerSideProps = async ({
    req,
    res,
    query: oldQuery,
}: ServerSideInitProps): Promise<ServerSideProps> => {
    // TODO: po otestování na produkci smazat – na public hostu povolit i nepřihlášené.
    const { api, discipline } = await getGlobalProps(
        req,
        res,
        isCoachesPublicRequest(req)
    );

    // TODO: tento if po otestování na produkci Smazat.
    if (!isCoachesRequestAllowed(req, res)) {
        serverRedirect('/home', res);
        return {};
    }

    const query = getPureQuery(oldQuery);
    const publishId = String(query.id ?? '');
    if (!publishId) {
        return { notFound: true };
    }

    const coachRaw: CoachDetailApiInterface = await getData(
        res,
        req,
        api.coach().coachDetail(publishId)
    );
    if (!coachRaw) {
        return { notFound: true };
    }

    const { personBase, coach } = buildCoachPersonBase(coachRaw, discipline);
    const config = buildCoachMatchesConfigStub();

    // Filter dropdowns are nice-to-have. The server-side helpers (getData /
    // getDataData / getCompetitionsListData) call handleAclErrorResponse on
    // failure, which writes a redirect to /home directly into `res` before
    // throwing — so wrapping the helpers in try/catch is too late. Instead,
    // call the underlying axios methods here and swallow errors locally so a
    // single 404/403 on /list/competition-years, /clubs or /competitions can
    // never redirect the whole matches page.
    async function safeAxios<T>(
        callApi: () => Promise<{ data: { data?: T } | T }>
    ): Promise<T> {
        try {
            const result = await callApi();
            const body = result?.data as { data?: T } | T;
            // Most list endpoints return { data, meta }; some return a raw array.
            const inner = (body as { data?: T })?.data;
            return (
                (inner !== undefined ? inner : (body as T)) ??
                ([] as unknown as T)
            );
        } catch {
            return [] as unknown as T;
        }
    }

    // Strip junk values that occasionally end up in the URL (e.g. literal
    // "undefined" / "null" strings from a stale getActualYear bug or from a
    // user clearing a filter manually). Sending them to the BE causes a 500
    // because the model binder cannot parse them as long?/int?.
    const cleanedQuery: NextApiRequestQuery = Object.fromEntries(
        Object.entries(query).filter(
            ([, v]) =>
                v !== undefined &&
                v !== null &&
                v !== '' &&
                v !== 'undefined' &&
                v !== 'null'
        )
    );

    const competitionYears = await safeAxios<CompetitionYearInterface[]>(() =>
        api.lists().competitionYears()
    );

    // No default ročník — coaches often have only historical matches; pre-filtering
    // to the active season would show an empty grid on first load.
    const filterInitQuery: NextApiRequestQuery = {};

    const matchesFilterQuery = buildCoachMatchesFilterQuery({
        ...cleanedQuery,
        id: undefined,
    });

    let matchesResponse: {
        data: CoachMatchListItemApiInterface[];
        meta: PaginatorInterface;
    };
    try {
        const { data } = await api
            .coach()
            .coachMatches(publishId, matchesFilterQuery);
        matchesResponse = {
            data: data?.data ?? [],
            meta: data?.meta ?? EMPTY_PAGINATOR,
        };
    } catch {
        // Bad filter params must show an empty grid, not a crash page.
        matchesResponse = { data: [], meta: EMPTY_PAGINATOR };
    }

    // The "Klub" and "Soutěž" filter dropdowns are scoped to this coach
    // (clubs they coached / competitions they trained in) instead of the
    // global lists, per FE spec. Falls back to empty arrays if any of the
    // calls fail so a single 4xx never breaks the matches page.
    const [coachClubsResp, coachCompetitionsResp] = await Promise.all([
        safeAxios<CoachClubListItemApiInterface[]>(() =>
            api.coach().coachClubs(publishId, { page_size: 200 })
        ),
        safeAxios<CoachCompetitionApiInterface[]>(() =>
            api.coach().coachCompetitions(publishId)
        ),
    ]);

    // Coach club rows → ClubListItemInterface for the shared autocomplete.
    // We only keep rows with a non-empty `club_internal_id` because the
    // matches BE filter binds on `Is1Oddil.IdOddil`, and we de-dupe so a
    // coach with multiple stints at one club appears only once.
    const seenClubIds = new Set<string>();
    const clubList: ClubListItemInterface[] = (coachClubsResp ?? [])
        .filter((row) => !!row.club_internal_id)
        .filter((row) => {
            const key = String(row.club_internal_id);
            if (seenClubIds.has(key)) return false;
            seenClubIds.add(key);
            return true;
        })
        .map((row) => ({
            internal_id: String(row.club_internal_id),
            name: row.club_name ?? '',
            id: row.club_id,
        }));

    const competitionList: CompetitionInterface[] = (
        coachCompetitionsResp ?? []
    ).map((c) => ({
        id: String(c.id),
        name: c.name ?? '',
        identifier: c.identifier ?? undefined,
    }));

    const props = await getPageProps<Props>({
        givenProps: {
            matches: matchesResponse.data,
            coach,
            config,
            paginator: matchesResponse.meta,
            personBase,
            competitionList,
            competitionYears,
            clubList,
            filterInitQuery,
        },
        req,
        res,
        api,
        query,
        nameSpaces: getPageNamespaces('/coaches/{0}/matches'),
    });

    return { props };
};

export default Page;
